<template>
<div>
    <l-map
      v-if="showMap"
      :zoom="zoom"
      :center="center"
    >
      <l-tile-layer
        :url="url"
      />
      <l-marker
        v-for="marker in currMarkers"
        :key="marker.id"
        :lat-lng="marker.position"
      >
        <l-popup :content="marker.tooltip" />
        <l-tooltip :content="marker.tooltip" />
      </l-marker>
    </l-map>

  </div>
</template>

<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { LMap, LMarker, LPopup, LTileLayer, LTooltip } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import { Icon } from "leaflet";

import {
  convertDMSToLatLong,
  convertUTMToLatLong,
} from "@/utils/convertGeoPositionToLatLong";

delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "MultipleMaps",
  props: {
    markers: {
      type: Array,
      required: true
    }
  },
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LPopup,
  },
  data() {
    return {
      showMap: false,
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      zoom: 8,
      center: null,
      currMarkers: []
    };
  },
  watch: {
    markers: {
      handler: 'formatPositionData',
      immediate: true
    }
  },
  methods: {
    formatPositionData(newVal) {
      let formattedMarkers = [];
      newVal.forEach((marker, index) => {
        const markerMapped = this.getPosition(marker)
        if(markerMapped != null) {
          // Set first marker as center by default
          if(index == 0) {
            this.center = markerMapped
          }
          
          // Set other data required
          const [lat, lng] = markerMapped;
          formattedMarkers.push({
            id: index,
            position: {lat, lng},
            tooltip: marker.project_code
          })
        }
      });
      this.currMarkers = formattedMarkers;
      this.showMap = this.currMarkers.length > 0;
    },
    getPosition(marker) {
      const { marker_data, coordinate_type } = marker;
      const { latitude, longitude, utm_zone, utm_easting, utm_northing } = marker_data;

      switch (coordinate_type) {
        case 'dd':
          return this.validateDecimalDegreesCoordinates(latitude, longitude);
          break;
        case 'dms':
          return this.validateDegreesMinutesSecondsCoordinates(latitude, longitude);
          break;
        case 'utm':
          return this.validateUTMCoordinates(utm_easting, utm_northing, utm_zone)
          break;
        default:
          this.showToast("Une erreur est survenue lors de l'ouverture de la visualisation.", 'AlertTriangleIcon', 'warning');
          return null;
          break;
      }
    },
    validateDecimalDegreesCoordinates(lat, lng) {
      // Checks if the coordinates are valid decimal degrees
      const isValidLat = typeof lat === "number" && lat >= -90 && lat <= 90;
      const isValidLng = typeof lng === "number" && lng >= -180 && lng <= 180;

      if (isValidLat && isValidLng) {
        return [lat, lng];
      }
      this.showToast(
        "Coordonnées en Dégrée Décimal invalides",
        "AlertTriangleIcon",
        "danger"
      );

      return null;
    },
    validateDegreesMinutesSecondsCoordinates(lat, lng) {
      // Check if the value conforms to the format of degrees, minutes, and seconds

      const coords = convertDMSToLatLong(lat, lng);
      if (coords) {
        return [coords.latitude, coords.longitude];
      }
      this.showToast(
        "Erreur de conversion des coordonnées Degrée Minute Seconde",
        "AlertTriangleIcon",
        "danger"
      );
      return [null, null];
    },
    validateUTMCoordinates(easting, northing, zone) {
      // Check if value conforms to the format of UTM
      //  Zone should be between 1 and 60
      // const isValidZone = typeof zone === 'number' && zone >= 1 && zone <= 60;

      // Easting should be a positive number
      const isValidEasting =
        typeof easting === "number" && easting >= 100000 && easting <= 999999;

      // Northing should be a positive number (in the northern hemisphere)
      const isValidNorthing =
        typeof northing === "number" && northing >= 0 && northing <= 10000000;

      if (isValidEasting && isValidNorthing) {
        const coords = convertUTMToLatLong(
          easting,
          northing,
          zone.replace("N", "")
        );

        if (coords) {
          return [coords.latitude, coords.longitude];
        }
        this.showToast(
          "Erreur de conversion des coordonnées UTM",
          "AlertTriangleIcon",
          "danger"
        );
        return null;
      }
      this.showToast(
        "Coordonnées en UTM invalides",
        "AlertTriangleIcon",
        "danger"
      );
      return null;
    },
    async reverseGeocode(lat, lng) {
      try {
        // En utilisant l'API Nominatim (OpenStreetMap)
        const response = await axios.get(
          "https://nominatim.openstreetmap.org/reverse",
          {
            params: {
              lat,
              lon: lng,
              format: "json",
            },
          }
        );

        this.currentAddress = response.data.display_name || null;
      } catch (error) {
        console.error("Erreur lors du géodecodage :", error);
      }
    },
    showToast(message, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: icon,
          variant: variant,
        },
      });
    }
  },
};
</script>

<style lang="scss">
.vue2leaflet-map {
  &.leaflet-container {
    height: 500px;
    width: 100%;
  }
}
</style>
